/**
 * AppShell — top bar + sidebar + main content area.
 *
 * Pages render into the <Outlet/> in the main area. Add a right rail per
 * page when needed by composing a 2-col grid inside that page (rather
 * than always reserving rail space here).
 */
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { TopBar } from "@/components/app/topbar";
import { Sidebar } from "@/components/app/sidebar";
import { SyncStatusBar } from "@/components/app/sync-status-bar";
import {
  AiAgentDrawer,
  AiAgentLauncher,
} from "@/components/app/ai-agent-drawer";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

export function AppShell() {
  const animatedContentRef = useUiAutoAnimate();
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(() => !window.matchMedia("(min-width: 1024px)").matches);
  // Track screen size and initialize sidebar state
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const isDesktop = e.matches;
      setIsMobile(!isDesktop);

      if (!isDesktop) {
        // Switching to mobile: close mobile drawer
        setMobileSidebarOpen(false);
        setMobileSidebarVisible(false);
      }
    };

    mediaQuery.addEventListener("change", handleMediaChange);
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  function toggleDesktopSidebar() {
    setDesktopSidebarOpen((prev) => !prev);
  }

  function openMobileSidebar() {
    setMobileSidebarOpen(true);
  }

  function closeMobileSidebar() {
    setMobileSidebarVisible(false);
  }

  function handleSidebarToggle() {
    if (isMobile) {
      openMobileSidebar();
    } else {
      toggleDesktopSidebar();
    }
  }

  // Mobile drawer animation effects
  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const frame = requestAnimationFrame(() => setMobileSidebarVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [mobileSidebarOpen]);

  useEffect(() => {
    if (!mobileSidebarOpen || mobileSidebarVisible) return;
    const t = setTimeout(() => setMobileSidebarOpen(false), 220);
    return () => clearTimeout(t);
  }, [mobileSidebarOpen, mobileSidebarVisible]);

  const sidebarVisible = isMobile ? mobileSidebarOpen : desktopSidebarOpen;

  return (
    <div className="grid h-dvh w-full overflow-hidden grid-rows-[60px_1fr]">
      <TopBar
        onToggleSidebar={handleSidebarToggle}
        sidebarOpen={sidebarVisible}
      />
      <div className="grid min-h-0 h-full overflow-hidden lg:flex">
        {/* Desktop persistent sidebar with animated hide/show */}
        <div
          className={[
            "hidden lg:flex h-full shrink-0 overflow-hidden transition-[width,opacity,transform] duration-300 ease-out",
            desktopSidebarOpen
              ? "w-[252px] opacity-100 translate-x-0"
              : "w-0 opacity-0 -translate-x-4 pointer-events-none",
          ].join(" ")}
          aria-hidden={!desktopSidebarOpen}
        >
          <Sidebar onClose={toggleDesktopSidebar} />
        </div>
        <main
          ref={animatedContentRef}
          data-testid="app-main-scroll"
          className="h-full min-h-0 overflow-y-auto px-4 pt-4 pb-4 sm:px-6 sm:pt-5 sm:pb-16 lg:px-7 lg:pt-6 lg:pb-20 flex flex-col lg:flex-1 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-line/30 hover:[&::-webkit-scrollbar-thumb]:bg-line/50 [&::-webkit-scrollbar-track]:bg-transparent"
        >
          <SyncStatusBar />
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar drawer */}
      {mobileSidebarOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden pointer-events-none">
          <button
            type="button"
            aria-label="Dismiss sidebar"
            className={[
              "absolute inset-0 bg-bg-0/80 backdrop-blur-[1px] transition-opacity duration-200 pointer-events-auto",
              mobileSidebarVisible ? "opacity-100" : "opacity-0",
            ].join(" ")}
            onClick={closeMobileSidebar}
          />
          <div
            className={[
              "relative h-full w-[252px] max-w-[84vw] transition-transform duration-200 ease-out pointer-events-auto",
              mobileSidebarVisible ? "translate-x-0" : "-translate-x-full",
            ].join(" ")}
          >
            <Sidebar
              showCloseButton
              onClose={closeMobileSidebar}
              onNavigate={closeMobileSidebar}
            />
          </div>
        </div>
      ) : null}

      {/* AI agent UI lives at the shell level so it's reachable from every
          authenticated page. The launcher is a floating button; the drawer
          slides in from the right when toggled. Both are zero-cost when
          collapsed (drawer returns null). */}
      <AiAgentLauncher />
      <AiAgentDrawer />
    </div>
  );
}
